import React from 'react';
import { Line } from './namedEntityRecognitionParser';
import _ from 'lodash';
import { LineRange } from './namedEntityRecognitionSelection';

interface Box {
  left: number;
  top: number;
  height: number;
  width: number;
}

export function useLineRangePositions(lines: Line[], linesContainerBox?: Box) {
  // We keep track of the `Line` object each line element is supposed to correspond to, so that
  // we can detect when a lineElement goes out of date.
  const [lineElements, setLineElements] = React.useState<{
    [lineIdx: number]: { element: HTMLElement; line: Line };
  }>({});

  const setLineElement = React.useCallback(
    (lineIdx: number, lineElement: HTMLElement | null) => {
      // We don't really care about null elements, since we can determine when a line element is
      // out-of-date by checking whether its corresponding line is up-to-date.
      if (!lineElement) {
        return;
      }

      setLineElements(oldLineElements => {
        // This if statement is critical to ensure that we don't suffer from an infinite render loop.
        if (
          oldLineElements[lineIdx]?.element === lineElement &&
          oldLineElements[lineIdx]?.line === lines[lineIdx]
        ) {
          return oldLineElements;
        }

        return Object.assign({}, oldLineElements, {
          [lineIdx]: { element: lineElement, line: lines[lineIdx] },
        });
      });
    },
    [lines],
  );

  const lineElementsReady = _.every(lines, (line, lineIdx) => lineElements[lineIdx]?.line === line);

  const getLineRangeBoxes = React.useCallback(
    (lineRange: LineRange) => {
      if (!linesContainerBox || !lineElementsReady) {
        return [];
      }

      const rangeObj = document.createRange();
      const boxes = _.range(lineRange.startLineIdx, lineRange.endLineIdx + 1).map(lineIdx => {
        if (lines[lineIdx]?.value === '') {
          return null;
        }

        const lineTextNode = lineElements[lineIdx]?.element.childNodes[0] as Text;

        if (lineTextNode) {
          if (lineIdx === lineRange.startLineIdx && lineRange.startOffset < lineTextNode.length) {
            rangeObj.setStart(lineTextNode, lineRange.startOffset);
          } else {
            rangeObj.setStartBefore(lineTextNode);
          }
        }

        if (lineTextNode) {
          if (lineIdx === lineRange.endLineIdx && lineRange.endOffset < lineTextNode.length) {
            rangeObj.setEnd(lineTextNode, lineRange.endOffset);
          } else {
            rangeObj.setEndAfter(lineTextNode);
          }
        }

        const { top, left, height, width } = rangeObj.getBoundingClientRect();
        return {
          top: top - linesContainerBox.top,
          left: left - linesContainerBox.left,
          height,
          width,
        };
      });
      rangeObj.detach();
      return _.compact(boxes);
    },
    [lines, lineElements, lineElementsReady, linesContainerBox],
  );

  return {
    setLineElement,
    lineElementsReady,
    getLineRangeBoxes,
  };
}
